const SEEN_REVISION_KEY = "comodo.sessionHandoff.seenRevision.v1";
const PARTICIPANT_INTENT_KEY = "comodo.sessionHandoff.participantIntent.v1";

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface ParticipantHandoffIntent {
  revision: number;
  targetSessionId: string;
}

export function readSeenHandoffRevision(
  storage: StorageLike | null = browserSessionStorage(),
): number | null {
  const value = safeGet(storage, SEEN_REVISION_KEY);
  if (value === null) return null;
  const revision = Number(value);
  return Number.isSafeInteger(revision) && revision >= 0 ? revision : null;
}

export function writeSeenHandoffRevision(
  revision: number,
  storage: StorageLike | null = browserSessionStorage(),
): void {
  safeSet(storage, SEEN_REVISION_KEY, String(revision));
}

export function readParticipantHandoffIntent(
  targetSessionId: string,
  storage: StorageLike | null = browserSessionStorage(),
): ParticipantHandoffIntent | null {
  const raw = safeGet(storage, PARTICIPANT_INTENT_KEY);
  if (!raw) return null;
  try {
    const value = JSON.parse(raw) as Partial<ParticipantHandoffIntent>;
    if (
      !Number.isSafeInteger(value.revision) ||
      (value.revision ?? 0) < 1 ||
      typeof value.targetSessionId !== "string" ||
      value.targetSessionId !== targetSessionId
    ) {
      return null;
    }
    return { revision: value.revision!, targetSessionId: value.targetSessionId };
  } catch {
    return null;
  }
}

export function writeParticipantHandoffIntent(
  intent: ParticipantHandoffIntent,
  storage: StorageLike | null = browserSessionStorage(),
): void {
  safeSet(storage, PARTICIPANT_INTENT_KEY, JSON.stringify(intent));
}

export function clearParticipantHandoffIntent(
  targetSessionId: string,
  storage: StorageLike | null = browserSessionStorage(),
): void {
  const intent = readParticipantHandoffIntent(targetSessionId, storage);
  if (intent) safeRemove(storage, PARTICIPANT_INTENT_KEY);
}

function browserSessionStorage(): StorageLike | null {
  try {
    return typeof window === "undefined" ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

function safeGet(storage: StorageLike | null, key: string): string | null {
  try {
    return storage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function safeSet(storage: StorageLike | null, key: string, value: string): void {
  try {
    storage?.setItem(key, value);
  } catch {
    // Storage is an enhancement; callers retain in-memory fallbacks.
  }
}

function safeRemove(storage: StorageLike | null, key: string): void {
  try {
    storage?.removeItem(key);
  } catch {
    // Storage is an enhancement; callers retain in-memory fallbacks.
  }
}
